import React, {useCallback, useEffect, useState} from 'react';
import {formatDate as formatApiDate} from '../utils/dateTime';
import {fetchCurrentSubscription, QuotaUsage, Subscription} from '../service/SubscriptionService';
import createAxiosInstance from '../support/AxiosSupport';
import {getUpgradePortalUrl} from '../support/upgradeUrl';
import Spinner from '../components/Spinner';
import {CreditCardIcon} from '../components/icons';
import {useDocumentTitle} from '../hooks/useDocumentTitle';

/** Человекочитаемые названия типов квот (бэкенд отдаёт enum-код). */
const QUOTA_LABELS: Record<string, string> = {
    AI_REQUESTS: 'AI-запросы',
};

const quotaLabel = (type: string): string => QUOTA_LABELS[type] || type;

/** Тип квоты AI-запросов: единственный, чей расход зависит от источника AI. */
const AI_QUOTA_TYPE = 'AI_REQUESTS';

/** Фактически используемый источник AI (зеркало {@code UserSettingsDto.effectiveAiSource}). */
type AiSource = 'PLATFORM' | 'BYO' | null;

/**
 * Что именно списывает AI-квоту (R22). На экране висело «AI-запросы 0 / 50» у пользователя,
 * который работает через свой ключ или CLI-подписку, — без пояснения это читалось как
 * «лимит скоро кончится», хотя его запросы в квоту не попадают вообще.
 */
const aiQuotaNote = (source: AiSource): string => {
    const base = 'Считаются только запросы через платформенный AI; при своём ключе/CLI лимит не расходуется.';
    if (source === 'BYO') {
        return `${base} Сейчас используется ваш ключ/подписка — эти запросы лимит не тратят.`;
    }
    if (source === 'PLATFORM') {
        return `${base} Сейчас используется платформенный AI — запросы идут в этот лимит.`;
    }
    return base;
};

/** {@code limit === -1} — безлимит (CTHS-19). */
const isUnlimited = (limit: number): boolean => limit < 0;

const formatDate = (iso: string | null): string =>
    formatApiDate(iso, {day: 'numeric', month: 'long', year: 'numeric'});

/** Доля использования для прогресс-бара (0..1); для безлимита — 0 (бар не заполняем). */
const usedFraction = (q: QuotaUsage): number => {
    if (isUnlimited(q.limit) || q.limit === 0) {
        return 0;
    }
    return Math.min(1, Math.max(0, q.used / q.limit));
};

const QuotaRow: React.FC<{ quota: QuotaUsage; aiSource: AiSource }> = ({quota, aiSource}) => {
    const unlimited = isUnlimited(quota.limit);
    const fraction = usedFraction(quota);
    const nearLimit = !unlimited && fraction >= 0.8;
    return (
        <li
            className="rounded-xl border border-gray-200 dark:border-ink-700 p-4"
            data-testid={`quota-${quota.quotaType}`}
        >
            <div className="flex items-baseline justify-between gap-3">
                <span className="font-medium text-gray-900 dark:text-gray-100">{quotaLabel(quota.quotaType)}</span>
                <span className="text-sm text-gray-600 dark:text-gray-300" data-testid="quota-amount">
                    {unlimited
                        ? 'Безлимит'
                        : `${quota.used} / ${quota.limit} · осталось ${Math.max(0, quota.remaining)}`}
                </span>
            </div>
            {!unlimited && (
                <div className="mt-2 h-2 rounded-full bg-gray-100 dark:bg-ink-700 overflow-hidden">
                    <div
                        className={`h-full rounded-full ${nearLimit ? 'bg-amber-500' : 'bg-brand-500'}`}
                        style={{width: `${Math.round(fraction * 100)}%`}}
                    />
                </div>
            )}
            <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                Сброс: {formatDate(quota.periodEnd)}
            </div>
            {quota.quotaType === AI_QUOTA_TYPE && (
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400" data-testid="quota-ai-note">
                    {aiQuotaNote(aiSource)}
                </p>
            )}
        </li>
    );
};

/**
 * Экран «Подписка и лимиты» (CTHS-21). Показывает текущий тариф, остаток квот и дату сброса,
 * с кнопкой апгрейда на внешний portal ({@link getUpgradePortalUrl}) — единственное место в
 * приложении, ведущее наружу: все внутренние CTA апгрейда приводят сюда ({@code UPGRADE_URL}).
 * Данные — {@code GET /api/subscription/current}
 * (owner-scoped, только свои данные). Состояния: loading / error(retry) / загружено.
 *
 * Edge cases: подписка не активна (истекла/отменена) → нотис об откате на бесплатный тариф;
 * участник family-плана ({@code self === false}) → видит общий лимит, но не управляет тарифом
 * (кнопка апгрейда скрыта).
 */
const SubscriptionPage: React.FC = () => {
    const [data, setData] = useState<Subscription | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    // Источник AI нужен только для подписи к квоте: его недоступность не ломает экран —
    // подпись остаётся в общем виде, без «сейчас используется …».
    const [aiSource, setAiSource] = useState<AiSource>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(false);
        try {
            setData(await fetchCurrentSubscription());
        } catch (e) {
            setError(true);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        let cancelled = false;
        createAxiosInstance()
            .then(api => api.get<{effectiveAiSource?: AiSource}>('/profile/settings'))
            .then(res => {
                if (!cancelled) {
                    setAiSource(res.data?.effectiveAiSource ?? null);
                }
            })
            .catch(() => undefined);
        return () => {
            cancelled = true;
        };
    }, []);

    useDocumentTitle();

    useEffect(() => {
        load();
    }, [load]);

    if (loading) {
        return <Spinner/>;
    }

    if (error || !data) {
        return (
            <div className="p-6 text-center" data-testid="subscription-error">
                <p className="text-gray-600 dark:text-gray-300 mb-4">Не удалось загрузить данные подписки</p>
                <button onClick={load} className="px-4 py-2 rounded-xl bg-brand-600 text-white hover:bg-brand-700">
                    Повторить
                </button>
            </div>
        );
    }

    const sub = data;
    const isActive = sub.status === 'ACTIVE';
    const isMember = !sub.self; // участник чужой (family) подписки
    const canManagePlan = sub.self; // апгрейдить тариф может только владелец
    const portalUrl = getUpgradePortalUrl();

    return (
        <div className="p-4 md:p-6 space-y-6 max-w-3xl">
            <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Подписка и лимиты</h1>
                <p className="text-gray-500 dark:text-gray-400 mt-1">
                    Ваш тариф, остаток лимитов на текущий период и дата их сброса.
                </p>
            </div>

            {/* Текущий тариф */}
            <section
                className="rounded-2xl border border-gray-200 dark:border-ink-700 p-5 shadow-card"
                data-testid="current-plan"
            >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-brand-50 dark:bg-brand-900/40
                                        flex items-center justify-center text-brand-600 dark:text-brand-400">
                            <CreditCardIcon className="w-5 h-5"/>
                        </div>
                        <div>
                            <div className="text-xs uppercase tracking-wide text-gray-400 dark:text-gray-500">
                                Текущий тариф
                            </div>
                            <div className="text-lg font-semibold text-gray-900 dark:text-gray-100"
                                 data-testid="plan-name">
                                {sub.planName}
                            </div>
                        </div>
                    </div>
                    <span
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                            isActive
                                ? 'bg-brand-50 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300'
                                : 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                        }`}
                        data-testid="plan-status"
                    >
                        <span className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-brand-500' : 'bg-amber-500'}`}/>
                        {isActive ? 'Активна' : 'Не активна'}
                    </span>
                </div>

                <dl className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                    <div>
                        <dt className="text-gray-500 dark:text-gray-400">Гардеробов</dt>
                        <dd className="font-medium text-gray-900 dark:text-gray-100" data-testid="plan-max-wardrobes">
                            {sub.maxWardrobes < 0 ? 'Безлимит' : sub.maxWardrobes}
                        </dd>
                    </div>
                    {sub.maxFamilyMembers > 0 && (
                        <div>
                            <dt className="text-gray-500 dark:text-gray-400">Участников семьи</dt>
                            <dd className="font-medium text-gray-900 dark:text-gray-100">{sub.maxFamilyMembers}</dd>
                        </div>
                    )}
                    {sub.expiresAt && (
                        <div>
                            <dt className="text-gray-500 dark:text-gray-400">Действует до</dt>
                            <dd className="font-medium text-gray-900 dark:text-gray-100">{formatDate(sub.expiresAt)}</dd>
                        </div>
                    )}
                </dl>
            </section>

            {/* Нотис: подписка не активна → откат на бесплатный тариф */}
            {!isActive && (
                <div
                    role="status"
                    className="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20
                               p-4 text-sm text-amber-800 dark:text-amber-200"
                    data-testid="inactive-notice"
                >
                    Подписка истекла или отменена — действуют ограничения бесплатного тарифа.
                </div>
            )}

            {/* Нотис: участник семейного плана не управляет тарифом */}
            {isMember && (
                <div
                    role="status"
                    className="rounded-xl border border-gray-200 dark:border-ink-700 bg-gray-50 dark:bg-ink-800
                               p-4 text-sm text-gray-600 dark:text-gray-300"
                    data-testid="member-notice"
                >
                    Вы участник семейного плана «{sub.planName}». Лимиты общие на всю семью;
                    управление тарифом доступно только владельцу подписки.
                </div>
            )}

            {/* Лимиты */}
            <section data-testid="quotas">
                <h2 className="text-lg font-semibold mb-3 text-gray-900 dark:text-gray-100">Лимиты на период</h2>
                {sub.quotas.length === 0 ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400" data-testid="quotas-empty">
                        На вашем тарифе нет лимитируемых ресурсов.
                    </p>
                ) : (
                    <ul className="space-y-3">
                        {sub.quotas.map((q) => (
                            <QuotaRow key={q.quotaType} quota={q} aiSource={aiSource}/>
                        ))}
                    </ul>
                )}
            </section>

            {/* Апгрейд — единственная primary-кнопка региона; только для владельца и при наличии portal */}
            {canManagePlan && portalUrl && (
                <section className="rounded-2xl border border-gray-200 dark:border-ink-700 p-5
                                    flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                        <div className="font-medium text-gray-900 dark:text-gray-100">Нужно больше лимитов?</div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                            Перейдите на тариф PRO или FAMILY на внешнем портале.
                        </p>
                    </div>
                    <a
                        href={portalUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="upgrade-cta"
                        className="inline-flex items-center justify-center min-h-[44px] px-5 rounded-xl
                                   bg-brand-600 text-white font-medium hover:bg-brand-700 transition-colors
                                   whitespace-nowrap"
                    >
                        Обновить тариф
                    </a>
                </section>
            )}
        </div>
    );
};

export default SubscriptionPage;
